/**
 * @providesModule StoreInfo
 */

// ********************
// MAIN
// ********************

import {Navigator} from 'react-native-deprecated-custom-components';

// These are the Payment Providers currently supported by SDK in /v1/payments
type PaymentProvider = 'VANTIV' | 'BRAINTREE';

/*
 * Represents the common fields in data received from Server
 * in the `/v1/store` and `/v2/store` api.
 * NOTE: These are the ONLY fields in the `/v2/store` endpoint
 */
interface StoreOfferInfo {
  total_winnings: number;
  free_z_available: boolean;
  free_z_amount: number;
  free_z_cooldown: number;
  seconds_to_free_z: number;
  account_upgrade_required: boolean;
  limited_time_offers: Array<LTOInfo> | null;
  offers: Array<OfferInfo>;
  incentives: Array<Incentive>;
  virtual_currency_offers: Array<VCOfferInfo> | null;
  offer_sets?: Array<OfferSetInfo>;
  isACHOnlyDeposit?: boolean;
}

/*
 * Represents the additional data received from the `/v1/store` endpoint
 */
interface StoreInfoV1 extends StoreOfferInfo {
  stored_card: {
    last_four: string | null;
    zip: string | null;
  };
  payment_types: Array<string>;
  withdrawal_fee_threshold: number;
  withdrawal_fee: number;
  limited_time_offer: LTOInfo | null;
  apple_pay: ServerApplePayInfo | null;
  paypal: ServerPayPalInfo | null;
  eProtect?: EProtectInfo | null;
}

/*
 * Represents the combination of the `/v2/store` endpoint with `/v1/payments`
 */
interface StoreInfoV2 extends StoreOfferInfo, PaymentsInfo {}

interface StoredCard {
  id: number;
  last_four: string;
  zip: string;
  expiration_date: string;
  card_type: string;
}

interface BasePaymentsInfo {
  client_token: string;
  payment_provider: PaymentProvider;
  payment_types: Array<string>;
  withdrawal_fee: number;
  withdrawal_fee_threshold: number;
  apple_pay: ApplePayInfo | null;
  eProtect: EProtectInfo | null;
  open_withdrawal_requests: Array<Withdrawal>;
}

/*
 * Represents the response body for a GET to /v1/payments
 */
interface ServerPaymentsResponse extends BasePaymentsInfo {
  stored_cards: Array<StoredCard>;
  paypal: ServerPayPalInfo | null;
  venmo: ServerVenmoInfo | null;
  aeropay: ServerAeropayInfo | null;
  bank_accounts: Array<BankAccount>;
}

interface PaymentsInfo extends BasePaymentsInfo {
  stored_cards: Array<SavedCreditCard>;
  bank_accounts: Array<SavedBankAccount>;
  paypal: PayPalInfo | null;
  venmo: VenmoInfo | null;
  aeropay: ServerAeropayInfo | null;
}

/*
 * The JavaScript object that we will use to store the data. Takes in the server JSON representation
 * and does necessary conversions to make it be what we need for the code.
 *
 * Fields need to be accessed like storeInfo.data.payment_types
 */
class StoreInfoObject {
  data: StoreInfoV2;

  constructor(offerInfo: StoreOfferInfo, paymentsInfo: ServerPaymentsResponse) {
    const convertedPayments = {
      ...paymentsInfo,
      stored_cards: this._convertStoredPayments(paymentsInfo),
      apple_pay: this._convertApplePay(paymentsInfo),
      paypal: this._convertPayPal(paymentsInfo),
      venmo: this._convertVenmo(paymentsInfo),
      aeropay: this._convertAeropay(paymentsInfo),
      bank_accounts: this._convertBankAccounts(paymentsInfo),
    };
    const storeInfo = {
      ...offerInfo,
      ...convertedPayments,
    };
    storeInfo.apple_pay = this._convertApplePay(paymentsInfo);
    storeInfo.paypal = this._convertPayPal(paymentsInfo);
    storeInfo.venmo = this._convertVenmo(paymentsInfo);
    storeInfo.aeropay = this._convertAeropay(paymentsInfo);

    this.data = storeInfo;
  }

  private _convertAeropay(paymentsInfo: ServerPaymentsResponse): AeropayInfo | null {
    if (!paymentsInfo.aeropay) {
      return null;
    }

    const aeropay = Object.assign({}, paymentsInfo.aeropay) as AeropayInfo;
    aeropay.kind = 'Aeropay';
    return aeropay;
  }

  private _convertBankAccounts(paymentsInfo: ServerPaymentsResponse): Array<SavedBankAccount> {
    if (!paymentsInfo.bank_accounts?.length) {
      return [];
    }

    const savedBankAccounts = paymentsInfo.bank_accounts.map(
      (bankAccount: BankAccount): SavedBankAccount => {
        const savedBank = {
          ...bankAccount,
          kind: 'SavedBankAccount',
        };

        return savedBank as SavedBankAccount;
      }
    );

    // sorting saved bank accounts so the default is listed first in list
    return savedBankAccounts.sort((a, b) => Number(b.is_selected) - Number(a.is_selected));
  }

  private _convertStoredPayments(paymentsInfo: ServerPaymentsResponse): Array<SavedCreditCard> {
    // TODO - PS-14969 clean this up with the final server API call work
    // We have to spoof expiration dates until the endpoint is live
    return paymentsInfo.stored_cards.map((storedCard: StoredCard): SavedCreditCard => {
      const savedPayment: SavedCreditCard = {
        id: storedCard.id,
        expiration: {
          month: this._getExpirationMonthFromString(storedCard.expiration_date),
          year: this._getExpirationYearFromString(storedCard.expiration_date),
        },
        lastFour: storedCard.last_four,
        type: storedCard.card_type,
        kind: 'SavedCreditCard',
        paymentProvider: paymentsInfo.payment_provider,
      };

      return savedPayment as SavedCreditCard;
    });
  }

  private _convertApplePay(paymentsInfo: ServerPaymentsResponse): ApplePayInfo | null {
    if (!paymentsInfo.apple_pay) {
      return null;
    }
    const applePay = Object.assign({}, paymentsInfo.apple_pay) as ApplePayInfo;
    applePay.kind = 'ApplePay';
    return applePay;
  }

  private _convertPayPal(paymentsInfo: ServerPaymentsResponse): PayPalInfo | null {
    if (!paymentsInfo.paypal) {
      return null;
    }

    const payPal = Object.assign({}, paymentsInfo.paypal) as PayPalInfo;
    payPal.kind = 'PayPal';
    return payPal;
  }

  private _convertVenmo(paymentsInfo: ServerPaymentsResponse): VenmoInfo | null {
    if (!paymentsInfo.venmo) {
      return null;
    }

    const venmo = Object.assign({}, paymentsInfo.venmo) as VenmoInfo;
    venmo.kind = 'Venmo';
    return venmo;
  }

  private _getExpirationMonthFromString(expiration: string): string {
    if (expiration) {
      const expirationArray = expiration.toString().split('/');
      return expirationArray.length > 0 ? expirationArray[0] : '00';
    }
    return '00';
  }

  private _getExpirationYearFromString(expiration: string): string {
    if (expiration) {
      const expirationArray = expiration.toString().split('/');
      return expirationArray.length > 1 ? expirationArray[1] : '00';
    }
    return '00';
  }

  _convertOpenWithdrawals(withdrawals: Array<any>): void {
    this.data.open_withdrawal_requests = withdrawals as Array<Withdrawal>;
  }
}

// ********************
// OFFERS
// ********************

type Incentive = {
  ad_unit_portrait: string | null;
  description: string | null;
  name: string;
  z_value: number;
  download_game_id: string | null;
  picture_url: string | null;
  ad_provider: string | null;
  ad_unit_landscape: string | null;
  download_link: string | null;
  download_app_id: string | null;
  type: string | null;
  id: number | string;
};

type LTOInfo = {
  id: string;
  amount: number;
  bonus: number;
  type: string;
  end: string;
};

type VCOfferInfo = {
  product_id: string;
  localizedPrice: string;
  value: string;
};

type OfferInfo = {
  id: string;
  amount: number;
  bonus: number;
  best_value: boolean;
  is_interstitial_purchase: boolean;
  ticketz?: number;
  points?: number;
  interstitial_type?: string;
};

type OfferSetInfo = {
  id: string;
  first_time_only: boolean;
  extra_bonus_percentage: number;
  extra_bonus_min_deposit: number;
  extra_bonus_description: string;
};

type PromoInfo = {
  type: 'FIXED' | 'PERCENT';
  deposit_required: boolean;
  uses: number;
  code: string;
  cash_amount: number;
  z_amount: number;
  valid: boolean;
  lto_compatible: boolean;
  is_referral_promo_code: boolean;
};

type LegacyPromoInfo = {
  code: string;
  receiverAmount: number;
  senderAmountType: 'FIXED' | 'PERCENT';
};

// ********************
// PAYMENT TYPES
// ********************

type EProtectInfo = {
  paypage_id: string;
  url: string;
  enabled: boolean;
  threat_metrix_org_id?: string;
};

interface ServerPayPalInfo {
  environment: number;
  client_id: string;
  email?: string;
  payment_token_id: number;
}

interface PayPalInfo extends ServerPayPalInfo {
  kind: 'PayPal';
}

interface ServerVenmoInfo {
  user_id?: number;
  venmo_username: string;
  venmo_user_id?: boolean;
  is_stored: boolean;
  is_enabled: boolean;
  venmo_payment_token_id: number;
}

interface VenmoInfo extends ServerVenmoInfo {
  kind: 'Venmo';
}

interface ServerApplePayInfo {
  enabled: boolean;
  merchant_id: string;
  evm_enabled: boolean;
  '3ds_enabled': boolean | undefined;
  three_ds_enabled: boolean | undefined;
  stored: boolean;
}
interface ApplePayInfo extends ServerApplePayInfo {
  kind: 'ApplePay';
}

export interface BankAccount {
  id: string;
  bank_name: string;
  account_last_four: string;
  name: string;
  is_selected: string;
}

interface SavedBankAccount extends BankAccount {
  kind: 'SavedBankAccount';
}

interface LinkACHBankPromo {
  kind: 'LinkACHBankPromo';
}
interface ServerAeropayInfo {
  user_id: string | null;
  support_email: string | null;
  user_has_deposited: boolean;
  first_deposit_bonus_percentage: number | null;
  // RTP(REAL TIME PROCESSING) object is only present if feature is enabled via client property
  rtp: {
    eligible: boolean;
    withdrawal_amount_limit: number;
    daily_frequency_count_limit: number;
    daily_frequency_count: number;
  } | null;
}

interface AeropayInfo extends ServerAeropayInfo {
  kind: 'Aeropay';
}

// ********************
// CREDIT CARD TYPES
// ********************

interface CreditCard {
  expiration: {
    month: string | number;
    year: string | number;
  };
  type: string;
}

type ServerCreditCardType = 'visa' | 'mastercard' | 'discover' | 'amex' | 'unknown';

interface ServerNewCreditCard extends CreditCard {
  cardNumber: string;
  cvc: string;
  holderInfo?: CardHolderInfo;
  type: ServerCreditCardType;
  zip: string;
  storePaymentInfo: boolean;
}
interface NewCreditCard extends ServerNewCreditCard {
  kind: 'NewCreditCard';
  paymentProvider: PaymentProvider;
}

interface ServerSavedCreditCard extends CreditCard {
  id: number;
  lastFour: string;
}
interface SavedCreditCard extends ServerSavedCreditCard {
  kind: 'SavedCreditCard';
  paymentProvider: PaymentProvider;
}

type CardHolderInfo = {
  name: string;
  address: string;
  extendedAddress?: string;
  city: string;
  state: string;
  country?: string;
};

type PaymentInfo =
  | NewCreditCard
  | SavedCreditCard
  | ApplePayInfo
  | PayPalInfo
  | VenmoInfo
  | AeropayInfo
  | SavedBankAccount
  | LinkACHBankPromo;

export type PaymentInfoKind = PaymentInfo['kind'];

// ********************
// TRANSACTIONS
// ********************

type NewPaymentRequestBody = {
  payment_provider: 'BRAINTREE' | 'VANTIV';
  card_type: string | null;
  nonce: string;
  store_payment_info: boolean;
  deposit_offer_id: string | null;
  promo_code: string | null;
  device_data: string | null;
  interstitial_config_id: string | null;
  is_interstitial: boolean;
  extra_bonus_applied?: boolean;
};

type SavedPaymentDepositRequestBody = {
  payment_token_id: number;
  deposit_offer_id: string | null;
  promo_code: string | null;
  device_data: string | null;
  interstitial_config_id: string | null;
  is_interstitial: boolean;
  extra_bonus_applied?: boolean;
};

type NewPaymentResponse = {
  cash_balance: number;
  z_balance: number;
  ticketz_balance: number;
  ad_optimization_multiplier: number;
};

type StoredPaymentResponse = NewPaymentResponse;

type Transaction = {
  id: string;
  game_id: string;
  game_icon_url: string;
  cash_amount: number;
  bonus_cash_amount: number;
  cash_balance: number;
  z_amount: number;
  z_balance: number;
  ticketz_amount: number;
  ticketz_balance: number;
  created_at: string;
  type: string;
  match_id: number | null;
  user_id: string;
  ad_optimization_multiplier: number;
};

interface TransactionRequest {
  type: string;
  offer_id?: string;
  promo_code?: string;
  threat_metrix_session_id?: string | null | undefined;
  interstitial_config_id?: string | null;
  is_interstitial?: boolean;
  extra_bonus_applied?: boolean;
}

interface CardTransactionRequest extends TransactionRequest {
  store_payment_info: boolean;
}

interface NewCardTransaction extends CardTransactionRequest {
  card_number: string;
  security_code: string;
  exp_month: number;
  exp_year: number;
  zip: string;
  use_stored_payment_info: false;
}

interface SavedCardTransaction extends CardTransactionRequest {
  use_stored_payment_info: true;
  card_id: number;
}

// ********************
// MISCELLANEOUS
// ********************

interface ThreatMetrixInfo {
  threatMetrixOrgId?: string;
  threatMetrixVerified: boolean;
  threatMetrixRetryFunction: (completion: (success: boolean) => void) => void;
}

type ApplePayResponse = {
  status: number;
  confirmation: ApplePayResponseConfirmation;
};

type ApplePayResponseConfirmation = {
  version: string;
  header: {
    wrappedKey: string;
    transactionId: string;
    publicKeyHash: string;
    applicationData: string;
    ephemeralPublicKey: string;
  };
  signature: string;
  data: string;
};

type ThirdPartyPaymentPayload = {
  type: 'DEPOSIT_PAYPAL_CLIENT' | 'DEPOSIT_APPLE_PAY' | 'DEPOSIT_AEROPAY';
  offer_id?: string;
  promo_code?: string;
  threat_metrix_session_id?: string | null | undefined;
  is_interstitial?: boolean;
  interstitial_config_id?: string | null;
  extra_bonus_applied?: boolean;
} & (
  | {
      type: 'DEPOSIT_PAYPAL_CLIENT';
      payment_id: string;
    }
  | {
      type: 'DEPOSIT_APPLE_PAY';
      apple_pay_token: ApplePayResponseConfirmation;
    }
  | {
      type: 'DEPOSIT_AEROPAY';
      bank_account_id: string;
    }
);

type Withdrawal = {
  id: number;
  user_id: number;
  amount: number;
  fee: number;
  bonus: number;
  created_at: string;
  type: 'CHECK' | 'PAYPAL';
};

type BraintreeCardNonceRequest = {
  number: string;
  cvv: string;
  expirationMonth: string | number;
  expirationYear: string | number;
  postalCode: string;
  cardholderName?: string;
  firstName?: string;
  lastName?: string;
  streetAddress?: string;
  extendedAddress?: string;
  locality?: string;
  region?: string;
  countryCode?: string;
};

export {
  ApplePayInfo,
  ApplePayResponse,
  BraintreeCardNonceRequest,
  EProtectInfo,
  ThirdPartyPaymentPayload,
  LegacyPromoInfo,
  LTOInfo,
  NewCardTransaction,
  NewCreditCard,
  NewPaymentRequestBody,
  NewPaymentResponse,
  OfferInfo,
  OfferSetInfo,
  PaymentInfo,
  PaymentsInfo,
  PaymentProvider,
  PayPalInfo,
  VenmoInfo,
  PromoInfo,
  SavedCardTransaction,
  SavedCreditCard,
  SavedPaymentDepositRequestBody,
  ServerCreditCardType,
  ServerPaymentsResponse,
  StoreOfferInfo,
  StoreInfoV1,
  StoreInfoV2,
  StoreInfoObject,
  StoredPaymentResponse,
  ThreatMetrixInfo,
  Transaction,
  TransactionRequest,
  SavedBankAccount,
  LinkACHBankPromo,
};
